import { useMemo, useState } from 'react';

export type FolderRow = {
  id: number;
  parent_id: number | null;
  name: string;
};
export type DocRow = {
  id: number;
  title: string;
  folder_id: number | null;
  status: string;
  urgency: string;
  versions_count: number;
};

type TreeNode = {
  id: number;
  name: string;
  children: TreeNode[];
  docs: DocRow[];
};

function buildTree(folders: FolderRow[], docs: DocRow[]): {
  roots: TreeNode[];
  rootDocs: DocRow[];
} {
  const byId = new Map<number, TreeNode>();
  folders.forEach((f) => byId.set(f.id, { id: f.id, name: f.name, children: [], docs: [] }));
  const roots: TreeNode[] = [];
  folders.forEach((f) => {
    const node = byId.get(f.id)!;
    if (f.parent_id === null) roots.push(node);
    else {
      const p = byId.get(f.parent_id);
      if (p) p.children.push(node);
      else roots.push(node);
    }
  });
  const rootDocs: DocRow[] = [];
  docs.forEach((d) => {
    if (d.folder_id === null) rootDocs.push(d);
    else byId.get(d.folder_id)?.docs.push(d);
  });
  const sortDocs = (ds: DocRow[]) => ds.sort((a, b) => a.title.localeCompare(b.title));
  const sortNode = (n: TreeNode) => {
    n.children.sort((a, b) => a.name.localeCompare(b.name));
    sortDocs(n.docs);
    n.children.forEach(sortNode);
  };
  roots.sort((a, b) => a.name.localeCompare(b.name));
  roots.forEach(sortNode);
  sortDocs(rootDocs);
  return { roots, rootDocs };
}

export default function FolderTree({
  folders,
  docs,
  onDocClick,
  onAddFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveDoc,
  onDepositInFolder,
}: {
  folders: FolderRow[];
  docs: DocRow[]; // seuls les validés
  onDocClick: (docId: number) => void;
  onAddFolder: (name: string, parentId: number | null) => void | Promise<void>;
  onRenameFolder: (id: number, name: string) => void | Promise<void>;
  onDeleteFolder: (id: number) => void | Promise<void>;
  onMoveDoc: (docId: number, folderId: number | null) => void | Promise<void>;
  onDepositInFolder?: (folderId: number | null, file: File) => void | Promise<void>;
}) {
  const { roots, rootDocs } = useMemo(() => buildTree(folders, docs), [folders, docs]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set(folders.map((f) => f.id)));

  function toggle(id: number) {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  }

  function FolderNode({ node, depth }: { node: TreeNode; depth: number }) {
    const [addingChild, setAddingChild] = useState(false);
    const [childName, setChildName] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [newName, setNewName] = useState(node.name);

    const open = expanded.has(node.id);

    return (
      <div
        style={{ paddingLeft: depth === 0 ? 0 : 14 }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        onDrop={(e) => {
          e.preventDefault();
          const docId = Number(e.dataTransfer.getData('text/plain'));
          if (docId) onMoveDoc(docId, node.id);
        }}
      >
        <div className="folder-row">
          <button
            className="folder-expand"
            onClick={() => toggle(node.id)}
            title={open ? 'Replier' : 'Déplier'}
          >
            {open ? '▾' : '▸'}
          </button>
          <span className="folder-icon">{open ? '📂' : '📁'}</span>
          {renaming ? (
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={() => {
                if (newName.trim() && newName !== node.name) onRenameFolder(node.id, newName.trim());
                setRenaming(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  setNewName(node.name);
                  setRenaming(false);
                }
              }}
              style={{ flex: 1, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--c-border)' }}
            />
          ) : (
            <span
              className="folder-name"
              onDoubleClick={() => setRenaming(true)}
              title="Double-clic pour renommer"
            >
              {node.name}
            </span>
          )}
          <span className="folder-count">{node.docs.length}</span>
          <div className="folder-actions">
            {onDepositInFolder && (
              <label
                title="Déposer un document directement ici (archive validée, sans workflow)"
                style={{ cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}
              >
                ⬇ Déposer
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onDepositInFolder(node.id, f);
                    e.currentTarget.value = '';
                  }}
                />
              </label>
            )}
            <button onClick={() => setAddingChild((v) => !v)} title="Nouveau sous-dossier">
              +
            </button>
            <button onClick={() => setRenaming(true)} title="Renommer">
              ✎
            </button>
            <button
              onClick={() => {
                if (confirm(`Supprimer le dossier « ${node.name} » ? Les documents remontent à la racine.`)) {
                  onDeleteFolder(node.id);
                }
              }}
              title="Supprimer"
            >
              ×
            </button>
          </div>
        </div>

        {addingChild && (
          <div className="folder-add-form" style={{ marginLeft: 30 }}>
            <input
              autoFocus
              placeholder="Nom du sous-dossier"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && childName.trim()) {
                  onAddFolder(childName.trim(), node.id);
                  setChildName('');
                  setAddingChild(false);
                }
                if (e.key === 'Escape') {
                  setAddingChild(false);
                  setChildName('');
                }
              }}
            />
          </div>
        )}

        {open && (
          <div>
            {node.children.map((c) => (
              <FolderNode key={c.id} node={c} depth={depth + 1} />
            ))}
            {node.docs.map((d) => (
              <DocRowView key={d.id} doc={d} depth={depth + 1} onClick={() => onDocClick(d.id)} />
            ))}
          </div>
        )}
      </div>
    );
  }

  function DocRowView({ doc, depth, onClick }: { doc: DocRow; depth: number; onClick: () => void }) {
    return (
      <div
        className="folder-doc-row"
        style={{ paddingLeft: depth * 14 + 30 }}
        draggable
        onDragStart={(e) => e.dataTransfer.setData('text/plain', String(doc.id))}
        onClick={onClick}
      >
        <span>📄</span>
        <span className="folder-doc-title">{doc.title}</span>
        <span className="card-meta" style={{ fontSize: 11 }}>
          {doc.versions_count} version{doc.versions_count > 1 ? 's' : ''}
        </span>
      </div>
    );
  }

  const [addingRoot, setAddingRoot] = useState(false);
  const [rootName, setRootName] = useState('');

  return (
    <div className="folder-tree">
      <div
        className="folder-tree-header"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const docId = Number(e.dataTransfer.getData('text/plain'));
          if (docId) onMoveDoc(docId, null);
        }}
      >
        <strong>📚 Archive validée</strong>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {onDepositInFolder && (
            <label
              className="btn btn-ghost"
              style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
              title="Déposer un document de référence directement validé à la racine"
            >
              ⬇ Déposer à la racine
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onDepositInFolder(null, f);
                  e.currentTarget.value = '';
                }}
              />
            </label>
          )}
          <button
            className="btn btn-ghost"
            onClick={() => setAddingRoot((v) => !v)}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            {addingRoot ? 'Annuler' : '+ Dossier racine'}
          </button>
        </div>
      </div>
      {addingRoot && (
        <div className="folder-add-form">
          <input
            autoFocus
            placeholder="Nom du dossier"
            value={rootName}
            onChange={(e) => setRootName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && rootName.trim()) {
                onAddFolder(rootName.trim(), null);
                setRootName('');
                setAddingRoot(false);
              }
              if (e.key === 'Escape') {
                setAddingRoot(false);
                setRootName('');
              }
            }}
          />
        </div>
      )}
      {roots.length === 0 && rootDocs.length === 0 && (
        <div className="card-meta" style={{ padding: 14 }}>
          Aucun document validé. Crée un dossier puis valide tes documents pour les classer ici.
        </div>
      )}
      {roots.map((r) => (
        <FolderNode key={r.id} node={r} depth={0} />
      ))}
      {rootDocs.length > 0 && (
        <div className="folder-root-docs">
          <div className="folder-row" style={{ opacity: 0.6 }}>
            <span className="folder-icon">🗂️</span>
            <span className="folder-name">Racine (non classés)</span>
            <span className="folder-count">{rootDocs.length}</span>
          </div>
          {rootDocs.map((d) => (
            <DocRowView key={d.id} doc={d} depth={0} onClick={() => onDocClick(d.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Sélecteur de dossier pour la modale de validation. */
export function FolderPicker({
  folders,
  value,
  onChange,
  onCreateFolder,
}: {
  folders: FolderRow[];
  value: number | null;
  onChange: (id: number | null) => void;
  onCreateFolder: (name: string, parentId: number | null) => Promise<number | null>;
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [parentForNew, setParentForNew] = useState<number | null>(value ?? null);

  const sorted = useMemo(() => {
    // Construit les chemins pour affichage : "Plans / Phase 32"
    const byId = new Map(folders.map((f) => [f.id, f]));
    function pathOf(id: number): string {
      const segs: string[] = [];
      let cur: number | null = id;
      for (let i = 0; i < 16 && cur !== null; i++) {
        const row = byId.get(cur);
        if (!row) break;
        segs.unshift(row.name);
        cur = row.parent_id;
      }
      return segs.join(' / ');
    }
    return folders
      .map((f) => ({ id: f.id, path: pathOf(f.id) }))
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [folders]);

  return (
    <div>
      <label className="card-meta" style={{ display: 'block', marginBottom: 4 }}>
        Ranger dans
      </label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={{
          width: '100%',
          padding: 10,
          border: '1px solid var(--c-border)',
          borderRadius: 6,
          background: 'var(--c-bg)',
        }}
      >
        <option value="">— Racine (non classé)</option>
        {sorted.map((f) => (
          <option key={f.id} value={f.id}>
            {f.path}
          </option>
        ))}
      </select>
      <div style={{ marginTop: 8 }}>
        {!creating ? (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: '6px 12px', fontSize: 13 }}
            onClick={() => setCreating(true)}
          >
            + Nouveau dossier
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              value={parentForNew ?? ''}
              onChange={(e) =>
                setParentForNew(e.target.value === '' ? null : Number(e.target.value))
              }
              style={{ padding: 8, border: '1px solid var(--c-border)', borderRadius: 6 }}
            >
              <option value="">racine</option>
              {sorted.map((f) => (
                <option key={f.id} value={f.id}>
                  dans {f.path}
                </option>
              ))}
            </select>
            <input
              autoFocus
              value={newName}
              placeholder="Nom du dossier"
              onChange={(e) => setNewName(e.target.value)}
              style={{
                flex: 1,
                padding: 8,
                border: '1px solid var(--c-border)',
                borderRadius: 6,
              }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                if (!newName.trim()) return;
                const id = await onCreateFolder(newName.trim(), parentForNew);
                if (id) onChange(id);
                setNewName('');
                setCreating(false);
              }}
            >
              Créer
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setCreating(false);
                setNewName('');
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
